import { ThemeType, ThemeConfig, getThemeConfig, DEFAULT_THEME } from './config';
import { setCSSImageVersion } from 'core/hooks/useImage';

// 获取当前主题
export const getTheme = (): ThemeType => {

    // 优先从h5_theme中获取
    const h5Theme = localStorage.getItem('h5_theme');
    if (h5Theme) return h5Theme as ThemeType;

    // 先从localStorage中获取，如果没有再从环境变量中获取
    const savedTheme = localStorage.getItem('app_theme');
    if (savedTheme) return savedTheme as ThemeType;
    return import.meta.env.VITE_THEME || DEFAULT_THEME;
};

// 根据主题返回对应的app版本
export const getAppVersion = (): string => {
    const theme = getTheme();
    switch (theme) {
        case 'green':
            return 'app_v2';
        case 'red':
        case 'red_v2':
            return 'app_v1';
        case 'purple':
            return 'app_v3';
        case 'yellow':
            return 'app_v4';
        case 'blue':
            return 'app_v5';
        case 'white':
            return 'app_v6';
        case 'deeppurple':
            return 'app_v7';
        case 'brown':
            return 'app_v8';
        case 'black':
            return 'app_v9';
        case 'star':
            return 'app_v10';
        default:
            return 'app_v2';
    }
};

// 预加载所有主题文件
const themeModules = import.meta.glob('./*.css');

// 动态加载主题CSS
export const loadTheme = (): void => {
    const themeType = getTheme();
    const themeConfig = getThemeConfig(themeType);
    const themePath = themeConfig.cssPath;

    // 检查主题是否存在
    if (themeModules[themePath]) {
        // 加载主题
        themeModules[themePath]()
            .then(() => {
                console.log(`主题 "${themeConfig.name}" 已加载`);

                // 移除旧主题的class
                document.body.classList.forEach(cls => {
                    if (cls.startsWith('theme-')) {
                        document.body.classList.remove(cls);
                    }
                });

                // 将当前主题添加到body的class中，方便根据主题应用不同样式
                document.body.classList.add(`theme-${themeType}`);

                // 在主题加载完成后设置图片版本号
                setTimeout(() => {
                    setCSSImageVersion();
                }, 100);
            })
            .catch(err => {
                console.error(`加载主题 "${themeConfig.name}" 失败:`, err);
                loadFallbackTheme(themeType);
            });
    } else {
        console.error(`主题CSS文件 "${themePath}" 不存在`);
        loadFallbackTheme(themeType);
    }
};

// 加载默认主题
const loadFallbackTheme = (currentTheme: ThemeType): void => {
    if (currentTheme !== DEFAULT_THEME) {
        const defaultThemeConfig = getThemeConfig(DEFAULT_THEME);
        if (themeModules[defaultThemeConfig.cssPath]) {
            console.log('尝试加载默认主题...');
            themeModules[defaultThemeConfig.cssPath]()
                .then(() => {
                    // 在默认主题加载完成后也设置图片版本号
                    setTimeout(() => {
                        setCSSImageVersion();
                    }, 100);
                })
                .catch(err => {
                    console.error('加载默认主题失败:', err);
                });
        }
    }
};

// 导出获取主题的方法
export const getCurrentTheme = (): ThemeType => {
    return getTheme();
};

// 更新主题色的meta标签
export const updateThemeColorMeta = (themeType: ThemeType): void => {
    const themeConfig = getThemeConfig(themeType);
    const themeColor = themeConfig.themeColor;

    // 更新theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute('content', themeColor);
    }

    // 更新msapplication-navbutton-color
    const metaNavButtonColor = document.querySelector('meta[name="msapplication-navbutton-color"]');
    if (metaNavButtonColor) {
        metaNavButtonColor.setAttribute('content', themeColor);
    }
};

// 切换主题
export const switchTheme = async (themeType: ThemeType): Promise<void> => {
    // 保存到localStorage
    localStorage.setItem('app_theme', themeType);

    const themeConfig = getThemeConfig(themeType);
    const themePath = themeConfig.cssPath;

    try {
        // 检查主题是否存在
        if (themeModules[themePath]) {
            // 加载主题
            await themeModules[themePath]();

            // 移除旧主题的class
            document.body.classList.forEach(cls => {
                if (cls.startsWith('theme-')) {
                    document.body.classList.remove(cls);
                }
            });

            // 将当前主题添加到body的class中
            document.body.classList.add(`theme-${themeType}`);

            // 更新主题颜色meta标签
            updateThemeColorMeta(themeType);

            // 在主题切换完成后设置图片版本号
            setTimeout(() => {
                setCSSImageVersion();
            }, 100);

            console.log(`主题已切换为 "${themeConfig.name}"`);
        } else {
            throw new Error(`主题CSS文件 "${themePath}" 不存在`);
        }
    } catch (err) {
        console.error(`切换主题 "${themeConfig.name}" 失败:`, err);
    }
};
